import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { Box, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Tooltip from './Tooltip';
import { colors, shadows, typography as themeTypography } from '../theme/designTheme';

// ─── Helpers to convert a hex to a URL-encoded SVG fill ───────────────────
const encHex = (hex) => hex.replace('#', '%23');

// ─── Light dot-grid background (very subtle) ──────────────────────────────
const DOT_GRID_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Ccircle cx='1' cy='1' r='0.8' fill='${encHex('#C8CCCF')}'/%3E%3C/svg%3E")`;

// ─── Inline SVG icons (neutral grey by default) ────────────────────────────
// All builders accept a color + optional canvas size / padding so we can tint
// for high-risk or target-subject variants while keeping consistent sizing.
const svgWrap = (inner, size, pad) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><g transform="translate(${pad},${pad})">${inner}</g></svg>`;

const buildPersonSvg = (c, size = 40, pad = 8) => svgWrap(
  `<circle cx="12" cy="8" r="4" fill="${c}"/><path fill="${c}" d="M4 21c0-4.418 3.582-8 8-8s8 3.582 8 8z"/>`,
  size, pad,
);

// Office / company building — three-row window grid + door
const buildCompanySvg = (c, size = 40, pad = 8) => svgWrap(
  `<g fill="none" stroke="${c}" stroke-width="1.8" stroke-linejoin="round">
     <path d="M3 22 V5 a1 1 0 0 1 1 -1 h16 a1 1 0 0 1 1 1 V22"/>
     <path d="M10 22 V15 h4 V22"/>
   </g>
   <g fill="${c}">
     <rect x="5.5" y="7" width="2.5" height="2.5" rx="0.3"/>
     <rect x="10.75" y="7" width="2.5" height="2.5" rx="0.3"/>
     <rect x="16" y="7" width="2.5" height="2.5" rx="0.3"/>
     <rect x="5.5" y="11.5" width="2.5" height="2.5" rx="0.3"/>
     <rect x="16" y="11.5" width="2.5" height="2.5" rx="0.3"/>
   </g>`,
  size, pad,
);

// Accounting firm — calculator (screen + 2×3 buttons)
const buildAccountingSvg = (c, size = 40, pad = 8) => svgWrap(
  `<rect x="4" y="3" width="16" height="18" rx="1.5" fill="none" stroke="${c}" stroke-width="1.8"/>
   <rect x="6" y="5.5" width="12" height="3" rx="0.3" fill="${c}"/>
   <g fill="${c}">
     <circle cx="8"  cy="12.5" r="1.1"/>
     <circle cx="12" cy="12.5" r="1.1"/>
     <circle cx="16" cy="12.5" r="1.1"/>
     <circle cx="8"  cy="16.5" r="1.1"/>
     <circle cx="12" cy="16.5" r="1.1"/>
     <circle cx="16" cy="16.5" r="1.1"/>
   </g>`,
  size, pad,
);

// Law firm — scales of justice
const buildLawSvg = (c, size = 40, pad = 8) => svgWrap(
  `<g fill="none" stroke="${c}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
     <line x1="12" y1="4.5" x2="12" y2="20"/>
     <line x1="5"  y1="7.5" x2="19" y2="7.5"/>
     <line x1="8"  y1="20"  x2="16" y2="20"/>
     <path d="M5 7.5 L3 13 h4 Z"/>
     <path d="M19 7.5 L17 13 h4 Z"/>
   </g>
   <circle cx="12" cy="4.5" r="1.4" fill="${c}"/>`,
  size, pad,
);

// Brokerage — briefcase
const buildBrokerageSvg = (c, size = 40, pad = 8) => svgWrap(
  `<rect x="3" y="7" width="18" height="13" rx="1.6" fill="none" stroke="${c}" stroke-width="1.8"/>
   <path d="M9 7 V5.5 a1 1 0 0 1 1 -1 h4 a1 1 0 0 1 1 1 V7" fill="none" stroke="${c}" stroke-width="1.8" stroke-linejoin="round"/>
   <line x1="3" y1="13" x2="21" y2="13" stroke="${c}" stroke-width="1.3"/>
   <rect x="10" y="12" width="4" height="2" rx="0.3" fill="${c}"/>`,
  size, pad,
);

// Asset / warehouse — peaked roof silo
const buildAssetSvg = (c, size = 40, pad = 8) => svgWrap(
  `<path d="M3 11 L12 4 L21 11 V20 H3 Z" fill="none" stroke="${c}" stroke-width="1.8" stroke-linejoin="round"/>
   <rect x="10" y="14" width="4" height="6" fill="${c}"/>
   <line x1="3" y1="20" x2="21" y2="20" stroke="${c}" stroke-width="1.8"/>`,
  size, pad,
);

const toDataUri = (svg) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

// Build the six category icons in three tints (neutral / high-risk / suspect)
const ICON = {
  person: {
    base:    toDataUri(buildPersonSvg(colors.textSecondary)),
    high:    toDataUri(buildPersonSvg(colors.critical)),
    suspect: toDataUri(buildPersonSvg(colors.accent, 46, 11)),
  },
  company: {
    base:    toDataUri(buildCompanySvg(colors.textSecondary)),
    high:    toDataUri(buildCompanySvg(colors.critical)),
    suspect: toDataUri(buildCompanySvg(colors.accent, 46, 11)),
  },
  accounting_firm: {
    base:    toDataUri(buildAccountingSvg(colors.textSecondary)),
    high:    toDataUri(buildAccountingSvg(colors.critical)),
    suspect: toDataUri(buildAccountingSvg(colors.accent, 46, 11)),
  },
  law_firm: {
    base:    toDataUri(buildLawSvg(colors.textSecondary)),
    high:    toDataUri(buildLawSvg(colors.critical)),
    suspect: toDataUri(buildLawSvg(colors.accent, 46, 11)),
  },
  brokerage: {
    base:    toDataUri(buildBrokerageSvg(colors.textSecondary)),
    high:    toDataUri(buildBrokerageSvg(colors.critical)),
    suspect: toDataUri(buildBrokerageSvg(colors.accent, 46, 11)),
  },
  asset: {
    base:    toDataUri(buildAssetSvg(colors.textSecondary)),
    high:    toDataUri(buildAssetSvg(colors.critical)),
    suspect: toDataUri(buildAssetSvg(colors.accent, 46, 11)),
  },
};

// Infer category from the legacy `type` field when a node hasn't been migrated
const categoryFromType = (type) => (type === 'Person' || type === 'director' ? 'person' : 'company');

// ─── Risk helpers ────────────────────────────────────────────────────────────
const riskLevel = (val) => {
  const v = parseFloat(val);
  if (!isNaN(v) && v > 0.5) return 'high';
  if (!isNaN(v) && v > 0)   return 'medium';
  return 'low';
};

const RISK_COLORS = {
  high:   { bg: colors.criticalBg, border: colors.critical, label: 'HIGH',   text: colors.critical },
  medium: { bg: colors.highBg,     border: colors.high,     label: 'MEDIUM', text: colors.high     },
  low:    { bg: colors.surface,    border: colors.border,   label: 'LOW',    text: colors.accent   },
};

// ────────────────────────────────────────────────────────────────────────────
const GraphView = ({ data, customers: _customersLegacy, onNodeClick, primeSuspectId, caseType }) => {
  const cyRef              = useRef(null);
  const containerRef       = useRef(null);
  const [showIcons, setShowIcons]               = useState(false);
  const [nodePositions, setNodePositions]       = useState(null);
  const [selectedNodeId, setSelectedNodeId]     = useState(null);
  const [riskMode, setRiskMode]                 = useState(null);
  const [filteredElements, setFilteredElements] = useState([...data.nodes, ...data.edges]);
  const [tooltipData, setTooltipData]           = useState(null);

  // Reset cached positions whenever the graph data changes so CoSE re-runs
  // a fresh force-directed layout instead of reusing stale coordinates.
  const nodeIdKey = useMemo(
    () => data.nodes.map((n) => n.data.id).sort().join(','),
    [data.nodes],
  );

  useEffect(() => {
    setFilteredElements([...data.nodes, ...data.edges]);
    setRiskMode(null);
    setNodePositions(null);
  }, [nodeIdKey, data.edges]);

  const [suspectOverlayPos, setSuspectOverlayPos] = useState(null);
  const [flagOverlays, setFlagOverlays]           = useState([]);

  const nodeDataMap = useMemo(() => {
    const map = {};
    (data.nodes || []).forEach((n) => {
      map[n.data.id] = {
        customer_id: n.data.id,
        customer_name: n.data.label,
        person_or_business: n.data.entity_type || 'Person',
        nationality: n.data.nationality,
        country_of_incorporation: n.data.country_of_incorporation,
        date_of_birth: n.data.date_of_birth,
      };
    });
    return map;
  }, [data.nodes]);

  const updateSuspectOverlay = useCallback(() => {
    if (!cyRef.current || !primeSuspectId) return;
    const node = cyRef.current.getElementById(primeSuspectId);
    if (node.length) {
      const rPos = node.renderedPosition();
      const rH   = node.renderedHeight();
      setSuspectOverlayPos({ x: rPos.x, y: rPos.y + rH / 2 + 6 });
    }
  }, [primeSuspectId]);

  // Recompute screen positions for every node carrying status_flags
  const updateFlagOverlays = useCallback(() => {
    if (!cyRef.current) return;
    const overlays = [];
    cyRef.current.nodes().forEach((n) => {
      if (primeSuspectId && n.id() === primeSuspectId) return;
      const flags = n.data('status_flags');
      if (!Array.isArray(flags) || flags.length === 0) return;
      const rPos = n.renderedPosition();
      const rH   = n.renderedHeight();
      // Label sits ~18px below the node; pills sit below the label
      overlays.push({ id: n.id(), x: rPos.x, y: rPos.y + rH / 2 + 26, flags });
    });
    setFlagOverlays(overlays);
  }, [primeSuspectId]);

  useEffect(() => {
    if (!cyRef.current || !primeSuspectId || !nodePositions) return;
    const cy = cyRef.current;
    cy.on('viewport', updateSuspectOverlay);
    cy.on('drag', `node[id = "${primeSuspectId}"]`, updateSuspectOverlay);
    updateSuspectOverlay();
    return () => {
      cy.off('viewport', updateSuspectOverlay);
      cy.off('drag', `node[id = "${primeSuspectId}"]`, updateSuspectOverlay);
    };
  }, [primeSuspectId, nodePositions, updateSuspectOverlay]);

  // Keep status-flag overlays in sync with zoom, pan, and individual node drags
  useEffect(() => {
    if (!cyRef.current || !nodePositions) return;
    const cy = cyRef.current;
    cy.on('viewport', updateFlagOverlays);
    cy.on('drag', 'node', updateFlagOverlays);
    updateFlagOverlays();
    return () => {
      cy.off('viewport', updateFlagOverlays);
      cy.off('drag', 'node', updateFlagOverlays);
    };
  }, [nodePositions, updateFlagOverlays, data.nodes]);

  // ── Apply risk-based colours ───────────────────────────────────────────
  const applyNodeRiskColors = (cy) => {
    const highRiskNodeIds = new Set();

    cy.nodes().forEach((node) => {
      const isPrimeSuspect = primeSuspectId && node.id() === primeSuspectId;
      if (isPrimeSuspect) return;

      const level    = riskLevel(node.data('riskRating'));
      const category = node.data('category') || categoryFromType(node.data('type'));
      const iconSet  = ICON[category] || ICON.company;
      const rc       = RISK_COLORS[level];

      if (level === 'high') {
        highRiskNodeIds.add(node.id());
        node.addClass('high-risk');
        node.style({
          'background-color': rc.bg,
          'border-color':     rc.border,
          'border-width':     2,
          'background-image': iconSet.high,
        });
      } else if (level === 'medium') {
        node.style({
          'background-color': rc.bg,
          'border-color':     rc.border,
          'border-width':     1.5,
          'background-image': iconSet.base,
        });
      } else {
        node.style({
          'background-color': colors.surface,
          'border-color':     colors.border,
          'border-width':     1,
          'background-image': iconSet.base,
        });
      }
    });

    cy.edges().forEach((edge) => {
      const src = edge.data('source');
      const tgt = edge.data('target');

      if (highRiskNodeIds.has(tgt) || highRiskNodeIds.has(src)) {
        edge.style({
          'line-color': colors.critical,
          'line-opacity': 0.75,
          width: 1.6,
          'line-style': 'dashed',
        });
      } else {
        edge.style({
          'line-color': colors.textSecondary,
          'line-opacity': 0.65,
          width: 1.4,
          'line-style': 'dashed',
        });
      }
    });
  };

  // Pick an icon variant for the target subject based on its own category
  const suspectCategory = useMemo(() => {
    if (!primeSuspectId) return 'company';
    const n = (data.nodes || []).find((x) => x.data.id === primeSuspectId);
    return n?.data?.category || categoryFromType(n?.data?.type) || 'company';
  }, [primeSuspectId, data.nodes]);

  // ── Cytoscape stylesheet ───────────────────────────────────────────────
  const stylesheet = useMemo(() => [
    // Base node
    {
      selector: 'node',
      style: {
        'background-color': colors.surface,
        'border-color':     colors.border,
        'border-width':     1,
        width:  52,
        height: 52,
        label:             'data(label)',
        'font-size':       '14px',
        'font-family':     `${themeTypography.fontFamilyDisplay}, ${themeTypography.fontFamily}, sans-serif`,
        'font-weight':     600,
        'text-valign':     'bottom',
        'text-halign':     'center',
        'text-wrap':       'wrap',
        'text-max-width':  110,
        'text-margin-y':   10,
        color:             colors.textPrimary,
        'text-background-opacity': 0,
      },
    },
    // ── Category-driven node shapes + icons ─────────────────────────────
    // person → circle. Everything else → rounded square. Icon chosen by category.
    {
      selector: "node[category='person']",
      style: {
        shape: 'ellipse',
        'background-image':      ICON.person.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    {
      selector: "node[category='company']",
      style: {
        shape: 'roundrectangle',
        'background-image':      ICON.company.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    {
      selector: "node[category='accounting_firm']",
      style: {
        shape: 'roundrectangle',
        'background-image':      ICON.accounting_firm.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    {
      selector: "node[category='law_firm']",
      style: {
        shape: 'roundrectangle',
        'background-image':      ICON.law_firm.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    {
      selector: "node[category='brokerage']",
      style: {
        shape: 'roundrectangle',
        'background-image':      ICON.brokerage.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    {
      selector: "node[category='asset']",
      style: {
        shape: 'roundrectangle',
        'background-image':      ICON.asset.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    // Legacy fallbacks — old data without `category`
    {
      selector: "node[type='Person']",
      style: {
        shape: 'ellipse',
        'background-image':      ICON.person.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    {
      selector: "node[type='Business']",
      style: {
        shape: 'roundrectangle',
        'background-image':      ICON.company.base,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    },
    // Edges — default dashed mid-grey (darker than outlineVariant so relations pop)
    {
      selector: 'edge',
      style: {
        'line-color':         colors.textSecondary,
        'line-opacity':       0.65,
        'line-style':         'dashed',
        width:                1.4,
        'curve-style':        'bezier',
        'target-arrow-shape': 'none',
        label:                '',
      },
    },
    // Hover
    { selector: 'node:hover', style: { 'border-color': colors.accent, 'border-width': 2, 'z-index': 100 } },
    { selector: 'edge:hover', style: { 'line-color': colors.accent, 'line-opacity': 0.8, width: 1.8 } },
    // Selected
    { selector: '.selected', style: { 'border-color': colors.accent, 'border-width': 2.5, 'z-index': 999 } },
    // High-risk tint (non-suspect)
    {
      selector: `.high-risk${primeSuspectId ? `:not([id = "${primeSuspectId}"])` : ''}`,
      style: {
        'background-color': colors.criticalBg,
        'border-color':     colors.critical,
        'border-width':     2,
      },
    },
    // ── Prime suspect — target subject (blue ring, no intense glow) ────
    {
      selector: `node[id = "${primeSuspectId}"]`,
      style: {
        shape:             suspectCategory === 'person' ? 'ellipse' : 'roundrectangle',
        'background-color': colors.surface,
        'border-color':     colors.accent,
        'border-width':     3,
        width:  78,
        height: 78,
        'z-index': 1000,
        label:  '',
        'background-image':      (ICON[suspectCategory] || ICON.company).suspect,
        'background-fit':        'contain',
        'background-position-x': '50%',
        'background-position-y': '50%',
        'overlay-color':   colors.accent,
        'overlay-padding': 8,
        'overlay-opacity': 0.08,
      },
    },
  ], [primeSuspectId, suspectCategory]);

  useEffect(() => {
    if (cyRef.current && primeSuspectId) {
      cyRef.current.style().update();
    }
  }, [primeSuspectId]);

  // ── Main Cytoscape event wiring ────────────────────────────────────────
  useEffect(() => {
    if (!cyRef.current || !data) return;
    const cy = cyRef.current;

    cy.off('tap', 'node');
    cy.on('tap', 'node', (event) => {
      const node       = event.target;
      const customerId = node.id();
      const customer   = nodeDataMap[customerId];
      if (!customer) return;

      const enrichedCustomer = { ...customer, risk_rating: node.data('riskRating') || 'N/A' };
      const currentZoom = cy.zoom();
      const currentPan  = cy.pan();

      cy.nodes().removeClass('selected');
      node.addClass('selected');
      setSelectedNodeId(customerId);

      onNodeClick(enrichedCustomer);
      cy.zoom(currentZoom);
      cy.pan(currentPan);
    });

    cy.on('mouseover', 'edge', (event) => {
      const edge     = event.target;
      const edgeData = edge.data();

      // Prefer the specific relationship ("Shareholder", "Director",
      // "Spouse", ...). Fall back to the generic edge category when no
      // specific relationship is recorded (transaction edges).
      const prettify = (s) => (s || '')
        .toString()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      const relationshipLabel = prettify(edgeData.relationship) || edgeData.type || 'Relationship';
      let tooltipText = `Relationship: ${relationshipLabel}`;

      if (edgeData.type === 'Transaction') {
        const amt = edgeData.avg_txn_amount ?? edgeData.avg_transaction_amount;
        const amtLabel = amt != null
          ? `$${Number(amt).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
          : '—';
        tooltipText += `\nTransactions: ${edgeData.num_transactions ?? '—'}`;
        tooltipText += `\nAvg Amount: ${amtLabel}`;
      }
      setTooltipData({ text: tooltipText, position: event.renderedPosition });
    });
    cy.on('mouseout', 'edge', () => setTooltipData(null));

    applyNodeRiskColors(cy);

    if (!nodePositions) {
      cy.layout({
        name:           'cose',
        idealEdgeLength: 200,
        nodeRepulsion:   900000,
        gravity:         1,
        edgeElasticity:  100,
        nestingFactor:   1.2,
        padding:         50,
        animate:         false,
        randomize:       true,
        numIter:         500,
        nodeDimensionsIncludeLabels: true,
        fit:             true,
        stop: () => {
          const pos = {};
          cy.nodes().forEach((n) => { pos[n.id()] = n.position(); });
          setNodePositions(pos);
        },
      }).run();
    } else {
      cy.layout({
        name:      'preset',
        positions: nodePositions,
        animate:   false,
        fit:       false,
        zoom:      cy.zoom(),
        pan:       cy.pan(),
      }).run();
    }

    return () => {
      cy.off('tap', 'node');
      cy.off('mouseover', 'edge');
      cy.off('mouseout', 'edge');
    };
  }, [data, nodePositions, nodeDataMap, onNodeClick, primeSuspectId]);

  useEffect(() => {
    if (!cyRef.current || !filteredElements.length) return;
    const cy          = cyRef.current;
    const currentZoom = cy.zoom();
    const currentPan  = cy.pan();
    cy.layout({ name: 'preset', positions: nodePositions, animate: false, fit: false,
                zoom: currentZoom, pan: currentPan }).run();
    cy.zoom(currentZoom);
    cy.pan(currentPan);
  }, [filteredElements, nodePositions]);

  // ── Filter helpers ─────────────────────────────────────────────────────
  const getDirectHighRiskElements = () => {
    if (!selectedNodeId) return [...data.nodes, ...data.edges];
    const nodeIds    = new Set([selectedNodeId]);
    const directEdges = [];

    data.edges.forEach((edge) => {
      if (edge.data.source === selectedNodeId || edge.data.target === selectedNodeId) {
        const neighborId   = edge.data.source === selectedNodeId ? edge.data.target : edge.data.source;
        const neighborNode = data.nodes.find((n) => n.data.id === neighborId);
        if (neighborNode && parseFloat(neighborNode.data.riskRating || 0) >= 0.5) {
          nodeIds.add(neighborId);
          directEdges.push(edge);
        }
      }
    });

    return [...data.nodes.filter((n) => nodeIds.has(n.data.id)), ...directEdges];
  };

  const getIndirectHighRiskElements = () => {
    if (!selectedNodeId) return [...data.nodes, ...data.edges];

    const highRiskNodeIds = new Set(
      data.nodes.filter((n) => parseFloat(n.data.riskRating || 0) >= 0.5).map((n) => n.data.id)
    );
    const nodeIds = new Set([selectedNodeId]);
    const edgeIds = new Set();

    const findPaths = (startId) => {
      const queue   = [[startId, [startId]]];
      const visited = new Set();
      while (queue.length > 0) {
        const [currentId, path] = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        data.edges
          .filter((e) => e.data.source === currentId || e.data.target === currentId)
          .forEach((edge) => {
            const neighborId = edge.data.source === currentId ? edge.data.target : edge.data.source;
            if (!visited.has(neighborId)) {
              const newPath = [...path, neighborId];
              if (highRiskNodeIds.has(neighborId)) {
                newPath.forEach((id) => nodeIds.add(id));
                for (let i = 0; i < newPath.length - 1; i++) {
                  edgeIds.add(`${newPath[i]}-${newPath[i + 1]}`);
                  edgeIds.add(`${newPath[i + 1]}-${newPath[i]}`);
                }
              }
              queue.push([neighborId, newPath]);
            }
          });
      }
    };
    findPaths(selectedNodeId);

    return [
      ...data.nodes.filter((n) => nodeIds.has(n.data.id)),
      ...data.edges.filter((e) =>
        edgeIds.has(`${e.data.source}-${e.data.target}`) ||
        edgeIds.has(`${e.data.target}-${e.data.source}`)
      ),
    ];
  };

  // ── Toolbar handlers ───────────────────────────────────────────────────
  const handleDirectHighRisk = () => {
    if (riskMode === 'direct') { setRiskMode(null); setFilteredElements([...data.nodes, ...data.edges]); }
    else { setRiskMode('direct'); setFilteredElements(getDirectHighRiskElements()); }
  };
  const handleIndirectHighRisk = () => {
    if (riskMode === 'indirect') { setRiskMode(null); setFilteredElements([...data.nodes, ...data.edges]); }
    else { setRiskMode('indirect'); setFilteredElements(getIndirectHighRiskElements()); }
  };
  const handleResetGraph = () => {
    setRiskMode(null);
    setNodePositions(null);
    setFilteredElements([...data.nodes, ...data.edges]);
    setTimeout(() => {
      if (cyRef.current) {
        const cy = cyRef.current;
        applyNodeRiskColors(cy);
        cy.nodes().forEach((n) => n.removeClass('selected'));
        cy.style().update();
        cy.resize();
      }
    }, 50);
  };

  const handleZoomIn  = () => cyRef.current?.zoom({ level: cyRef.current.zoom() * 1.25, renderedPosition: { x: cyRef.current.width() / 2, y: cyRef.current.height() / 2 } });
  const handleZoomOut = () => cyRef.current?.zoom({ level: cyRef.current.zoom() / 1.25, renderedPosition: { x: cyRef.current.width() / 2, y: cyRef.current.height() / 2 } });
  const handleFit     = () => cyRef.current?.fit(undefined, 40);

  const TOOLBAR_BTNS = [
    { label: 'Direct\nHigh Risk',   mode: 'direct',   onClick: handleDirectHighRisk,        Icon: FilterAltOutlinedIcon,   needsNode: true  },
    { label: 'Indirect\nHigh Risk', mode: 'indirect', onClick: handleIndirectHighRisk,      Icon: AccountTreeOutlinedIcon, needsNode: true  },
    { label: 'Reset\nGraph',        mode: null,        onClick: handleResetGraph,            Icon: RefreshOutlinedIcon,     needsNode: false },
    { label: 'Show\nLayers',        mode: 'layers',    onClick: () => setShowIcons(v => !v), Icon: VisibilityOutlinedIcon,  needsNode: false, isToggle: true },
  ];

  const sidebarCustomer = selectedNodeId ? (nodeDataMap[selectedNodeId] ?? null) : null;
  const sidebarNode     = selectedNodeId ? data.nodes.find((n) => n.data.id === selectedNodeId) : null;
  const isPerson        = sidebarCustomer?.person_or_business === 'Person';
  const riskVal         = parseFloat(sidebarNode?.data.riskRating || 0);
  const rlevel          = riskLevel(riskVal);
  const rc              = RISK_COLORS[rlevel];

  const suspectName = primeSuspectId
    ? (nodeDataMap[primeSuspectId]?.customer_name || primeSuspectId)
    : null;

  // Re-measure Cytoscape when the parent container changes size (e.g. maximize toggle)
  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (cyRef.current) cyRef.current.resize();
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 320 }}>

      {/* ── Floating toolbar ─────────────────────────────────────────── */}
      <Box sx={{
        position: 'absolute',
        top: 14,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        display: 'flex',
        alignItems: 'stretch',
        bgcolor: colors.surface,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: shadows.card,
      }}>
        {TOOLBAR_BTNS.map(({ label, mode, onClick, needsNode, isToggle, Icon }, idx) => {
          const active    = isToggle ? showIcons : (mode && riskMode === mode);
          const disabled  = needsNode && !selectedNodeId;
          const accentClr = mode === 'direct'   ? colors.critical
                          : mode === 'indirect' ? colors.high
                          : colors.accent;

          return (
            <React.Fragment key={label}>
              {idx > 0 && (
                <Box sx={{ width: '1px', bgcolor: colors.borderSubtle, flexShrink: 0, my: 1 }} />
              )}
              <Box
                onClick={!disabled ? onClick : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.25,
                  py: 1.25,
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.35 : 1,
                  bgcolor: active ? colors.accentDim : 'transparent',
                  transition: 'background 0.15s, opacity 0.15s',
                  '&:hover': !disabled ? { bgcolor: active ? colors.accentDim : colors.surfaceElevated } : {},
                  userSelect: 'none',
                }}
              >
                <Icon sx={{
                  fontSize: 16,
                  color: active ? accentClr : colors.textMuted,
                  transition: 'color 0.15s',
                  flexShrink: 0,
                }} />
                <Typography sx={{
                  fontFamily: themeTypography.fontFamily,
                  fontSize: '11px',
                  fontWeight: active ? 700 : 500,
                  color: active ? accentClr : colors.textSecondary,
                  letterSpacing: '0.02em',
                  whiteSpace: 'pre-line',
                  lineHeight: 1.35,
                  transition: 'color 0.15s',
                }}>
                  {label}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>

      {/* ── Cytoscape canvas ─────────────────────────────────────────── */}
      <CytoscapeComponent
        elements={filteredElements}
        style={{
          width: '100%',
          height: '100%',
          background: `${DOT_GRID_BG}, ${colors.bg}`,
          borderRadius: '12px',
        }}
        stylesheet={stylesheet}
        cy={(cy) => { cyRef.current = cy; }}
        autoungrabify={false}
        autounselectify={true}
      />
      <Tooltip data={tooltipData} />

      {/* ── Prime-suspect React label overlay ────────────────────────── */}
      {suspectOverlayPos && primeSuspectId && suspectName && (
        <Box
          sx={{
            position: 'absolute',
            left:      suspectOverlayPos.x,
            top:       suspectOverlayPos.y,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex:    10,
            textAlign: 'center',
          }}
        >
          <Typography sx={{
            fontFamily:   themeTypography.fontFamilyDisplay,
            fontSize:     '13px',
            fontWeight:   700,
            color:        colors.textPrimary,
            letterSpacing:'-0.01em',
            lineHeight:   1.2,
            whiteSpace:   'nowrap',
          }}>
            {suspectName}
          </Typography>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: colors.accentDim,
            borderRadius: '999px',
            px: 1.25,
            py: 0.3,
            mt: 0.6,
          }}>
            <Typography sx={{
              fontFamily:   themeTypography.fontFamily,
              fontSize:     '9px',
              fontWeight:   700,
              color:        colors.accent,
              letterSpacing:'0.14em',
              whiteSpace:   'nowrap',
            }}>
              TARGET SUBJECT
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Status-flag badges (INSOLVENT, CASE #X, SANCTIONED, DIRECTOR …) ── */}
      {flagOverlays.map((o) => (
        <Box
          key={o.id}
          sx={{
            position: 'absolute',
            left:      o.x,
            top:       o.y,
            transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4,
            pointerEvents: 'none',
            zIndex: 9,
          }}
        >
          {o.flags.map((f, i) => {
            const tone =
              f.type === 'insolvent'     ? { bg: colors.criticalBg, fg: colors.critical } :
              f.type === 'sanctioned'    ? { bg: colors.criticalBg, fg: colors.critical } :
              f.type === 'related_case'  ? { bg: colors.highBg,     fg: colors.high     } :
              f.type === 'role'          ? { bg: colors.accentDim,  fg: colors.accent   } :
                                           { bg: colors.surfaceElevated, fg: colors.textSecondary };
            return (
              <Box
                key={i}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: tone.bg,
                  borderRadius: '999px',
                  px: 1,
                  py: 0.25,
                }}
              >
                <Typography sx={{
                  fontFamily:    themeTypography.fontFamily,
                  fontSize:      '9px',
                  fontWeight:    700,
                  letterSpacing: '0.12em',
                  color:         tone.fg,
                  whiteSpace:    'nowrap',
                }}>
                  {f.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}

      {/* ── Zoom controls ────────────────────────────────────────────── */}
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        right:  16,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.surface,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: shadows.card,
      }}>
        {[
          { label: '+', onClick: handleZoomIn  },
          { label: '−', onClick: handleZoomOut },
          { label: '⛶', onClick: handleFit     },
        ].map(({ label, onClick }, i) => (
          <React.Fragment key={label}>
            {i > 0 && <Box sx={{ height: '1px', bgcolor: colors.borderSubtle }} />}
            <Box
              onClick={onClick}
              sx={{
                width:  34,
                height: 34,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                color: colors.textSecondary,
                fontSize: label === '⛶' ? '13px' : '18px',
                fontWeight: 400,
                lineHeight: 1,
                transition: 'all 0.15s',
                '&:hover': { color: colors.accent, bgcolor: colors.accentDim },
                userSelect: 'none',
              }}
            >
              {label}
            </Box>
          </React.Fragment>
        ))}
      </Box>

      {/* ── Node detail sidebar ──────────────────────────────────────── */}
      <Box sx={{
        position: 'absolute',
        top:    58,
        right:  selectedNodeId ? 0 : -290,
        width:  248,
        maxHeight: 'calc(100% - 76px)',
        bgcolor: colors.surface,
        borderRadius: '12px 0 0 12px',
        transition: 'right 0.28s cubic-bezier(0.4,0,0.2,1)',
        zIndex: 100,
        overflowY: 'auto',
        boxShadow: shadows.elevated,
      }}>
        {selectedNodeId && sidebarCustomer && (
          <Box>
            <Box sx={{
              px: 2, pt: 2, pb: 1.5,
              bgcolor: colors.surfaceElevated,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
                <Box sx={{
                  width: 36, height: 36,
                  borderRadius: isPerson ? '50%' : '8px',
                  bgcolor: colors.accentDim,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {isPerson
                    ? <PersonIcon   sx={{ color: colors.accent, fontSize: 18 }} />
                    : <BusinessIcon sx={{ color: colors.accent, fontSize: 18 }} />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    color: colors.textPrimary, fontFamily: themeTypography.fontFamilyDisplay, fontWeight: 700,
                    fontSize: '13px', lineHeight: 1.2, letterSpacing: '-0.01em',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {sidebarCustomer.customer_name || selectedNodeId}
                  </Typography>
                  <Typography sx={{
                    fontFamily: themeTypography.fontFamily, fontSize: '9px', fontWeight: 700,
                    color: colors.textMuted, letterSpacing: '0.12em', mt: 0.3,
                  }}>
                    {isPerson ? 'INDIVIDUAL' : 'CORPORATE ENTITY'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 2, py: 1.5 }}>
              {[
                { label: 'CUSTOMER ID', value: selectedNodeId },
                caseType !== 'application_fraud'
                  ? { label: 'RISK LEVEL', value: rc.label, valueColor: rc.text }
                  : null,
                !isPerson
                  ? { label: 'INCORPORATED', value: sidebarCustomer.country_of_incorporation || '—' }
                  : null,
              ].filter(Boolean).map(({ label, value, valueColor }) => (
                <Box key={label} sx={{ mb: 1.4 }}>
                  <Typography sx={{
                    fontFamily: themeTypography.fontFamily, fontSize: '9px', fontWeight: 700,
                    letterSpacing: '0.1em', color: colors.textMuted, mb: 0.3,
                  }}>
                    {label}
                  </Typography>
                  <Typography sx={{
                    fontFamily: themeTypography.fontFamily, fontWeight: 600, fontSize: '12px',
                    color: valueColor || colors.textPrimary,
                  }}>
                    {value}
                  </Typography>
                </Box>
              ))}

              {caseType !== 'application_fraud' && (
                <>
                  <Box sx={{ height: '1px', bgcolor: colors.borderSubtle, my: 1.5 }} />

                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                      <Typography sx={{ fontFamily: themeTypography.fontFamily, fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', color: colors.textMuted }}>
                        RISK SCORE
                      </Typography>
                      <Typography sx={{ fontFamily: themeTypography.fontFamily, fontSize: '9px', fontWeight: 700, color: rc.text }}>
                        {(riskVal * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                    <Box sx={{ height: 4, bgcolor: colors.surfaceElevated, borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{
                        height: '100%',
                        width: `${Math.min(riskVal * 100, 100)}%`,
                        bgcolor: rc.text,
                        borderRadius: 2,
                        transition: 'width 0.4s ease',
                      }} />
                    </Box>
                  </Box>
                </>
              )}

              <Box
                onClick={() => { setSelectedNodeId(null); onNodeClick(null); }}
                sx={{
                  mt: 0.5, py: 0.9, borderRadius: '8px',
                  bgcolor: colors.surfaceElevated,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { bgcolor: colors.surfaceHover, color: colors.critical },
                }}
              >
                <Typography sx={{ fontFamily: themeTypography.fontFamily, fontSize: '10px', fontWeight: 700, color: colors.textSecondary, letterSpacing: '0.08em' }}>
                  DISMISS
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GraphView;
